import { el, clear, toast } from '../core/dom.js';
import { buildBackup, downloadBackup, parseBackup, restoreBackup } from '../storage/backup.js';
import { decryptBackup, encryptBackup, isEncryptedFile } from '../storage/crypto-backup.js';
import { buildDiagnostics } from '../storage/diagnostics.js';
import { logEvent, metaSet } from '../storage/db.js';
import { localDate, nowIso, tzOffsetMinutes } from '../core/time.js';
import { navigate } from '../router.js';
import { trackError } from '../analytics/ga.js';

export async function renderBackup(parent: HTMLElement) {
  clear(parent);
  parent.appendChild(el('div', { class: 'card' },
    el('h2', {}, 'Backup'),
    el('p', { class: 'muted' }, 'Your data lives on this device. Save a backup now and then.'),
    el('div', { class: 'stack' },
      el('button', { class: 'btn primary block', onclick: async (e: Event) => {
        const btn = e.currentTarget as HTMLButtonElement;
        if (btn.disabled) return;
        btn.disabled = true;
        try {
          await downloadBackup();
          await logEvent({
            type: 'backup_exported',
            occurredAt: nowIso(), localDate: localDate(),
            timezoneOffsetMinutes: tzOffsetMinutes(),
            payload: { encrypted: false },
          });
          toast('Backup downloaded.');
        } catch (err) {
          console.error('Backup export failed:', err);
          trackError('backup_export', err);
          toast('Backup failed — try again.');
        } finally {
          btn.disabled = false;
        }
      } }, 'Export plain JSON'),
      el('button', { class: 'btn block', onclick: () => exportEncrypted() }, 'Export encrypted (passphrase)'),
      el('button', { class: 'btn block', onclick: () => importPicker(parent) }, 'Import backup'),
    ),
  ));

  parent.appendChild(el('div', { class: 'card' },
    el('h2', {}, 'About backups'),
    el('p', { class: 'muted' }, 'Plain backups are readable JSON. Encrypted backups use AES-GCM with a key derived from your passphrase (PBKDF2-SHA256, 250k iterations). Lose the passphrase = lose the data — there is no recovery.'),
  ));

  parent.appendChild(await integrityCard());
}

async function integrityCard(): Promise<HTMLElement> {
  const card = el('div', { class: 'card' }, el('h2', {}, 'Data integrity'));
  try {
    const d = await buildDiagnostics();
    const i = d.integrity;
    const issues: Array<[string, number]> = [
      ['Habits missing domain', i.habitsMissingDomain],
      ['Active habits without methods', i.activeHabitsMissingMethod],
      ['Methods orphaned', i.methodsOrphaned],
      ['Templates orphaned to habit', i.purchaseTemplatesOrphanedHabit],
      ['Templates orphaned to method', i.purchaseTemplatesOrphanedMethod],
      ['Invalid purchase events', i.invalidPurchaseEvents],
    ];
    const nonzero = issues.filter(([, n]) => n > 0);
    if (nonzero.length === 0) {
      card.appendChild(el('p', { class: 'muted' }, 'All clear. No integrity issues detected.'));
    } else {
      card.appendChild(el('p', { class: 'muted' }, 'Phase 2 integrity checks. Non-zero counts indicate stale or orphaned records.'));
      for (const [label, n] of nonzero) {
        card.appendChild(el('div', { attrs: { style: 'display:flex; justify-content:space-between; padding:2px 0;' } },
          el('span', {}, label),
          el('span', { attrs: { style: 'color: var(--warn);' } }, String(n)),
        ));
      }
    }
  } catch (err) {
    card.appendChild(el('p', { class: 'muted' }, `Could not compute diagnostics: ${String(err)}`));
  }
  return card;
}

async function exportEncrypted() {
  const passphrase = prompt('Choose a passphrase (8+ characters):');
  if (!passphrase) return;
  if (passphrase.length < 8) { toast('Passphrase too short.'); return; }
  const confirm = prompt('Confirm passphrase:');
  if (confirm !== passphrase) { toast('Passphrases did not match.'); return; }
  try {
    const env = await buildBackup();
    const enc = await encryptBackup(env, passphrase);
    const blob = new Blob([JSON.stringify(enc, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const ts = new Date().toISOString().replace(/[:.]/g, '-');
    a.href = url; a.download = `wean-backup-${ts}.enc.json`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    await metaSet('last_backup_at', nowIso());
    await logEvent({
      type: 'backup_exported',
      occurredAt: nowIso(), localDate: localDate(),
      timezoneOffsetMinutes: tzOffsetMinutes(),
      payload: { encrypted: true },
    });
    toast('Encrypted backup downloaded.');
  } catch (err) {
    console.error('Encrypted export failed:', err);
    trackError('backup_export_encrypted', err);
    toast('Backup failed — try again.');
  }
}

function countByStore(data: Record<string, unknown[]>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(data)) out[k] = Array.isArray(v) ? v.length : 0;
  return out;
}

function importPicker(parent: HTMLElement) {
  const input = el('input', { type: 'file', attrs: { accept: 'application/json' } }) as HTMLInputElement;
  input.onchange = async () => {
    const f = input.files?.[0];
    if (!f) return;
    const text = await f.text();
    try {
      if (isEncryptedFile(text)) {
        const passphrase = prompt('Enter backup passphrase:');
        if (!passphrase) return;
        const enc = JSON.parse(text);
        const env = await decryptBackup(enc, passphrase);
        await renderImportPreview(parent, env, text.length);
      } else {
        const env = parseBackup(text);
        await renderImportPreview(parent, env, text.length);
      }
    } catch (e) {
      toast((e as Error).message || 'Invalid backup file.');
    }
  };
  input.click();
}

async function renderImportPreview(parent: HTMLElement, env: ReturnType<typeof parseBackup>, byteLength: number) {
  clear(parent);
  parent.appendChild(el('div', { class: 'card' },
    el('h2', {}, 'Import preview'),
    el('p', { class: 'muted' }, `Created ${new Date(env.createdAt).toLocaleString()}.`),
    el('pre', { attrs: { style: 'max-height:200px; overflow:auto; background:var(--surface-2); padding:0.75rem; border-radius:0.5rem;' } },
      JSON.stringify(countByStore(env.data), null, 2)),
    el('div', { class: 'stack' },
      el('button', { class: 'btn primary block', onclick: () => doRestore('replace') }, 'Replace existing data'),
      el('button', { class: 'btn block', onclick: () => doRestore('merge') }, 'Merge into existing data'),
      el('button', { class: 'btn ghost block', onclick: () => renderBackup(parent) }, 'Cancel'),
    ),
  ));
  let restoreInFlight = false;
  async function doRestore(mode: 'replace' | 'merge') {
    if (restoreInFlight) return;
    if (mode === 'replace' && !confirm('Replace will overwrite all current local data. Continue?')) return;
    restoreInFlight = true;
    try {
      await restoreBackup(env, mode);
      await logEvent({
        type: 'backup_imported',
        occurredAt: nowIso(), localDate: localDate(),
        timezoneOffsetMinutes: tzOffsetMinutes(),
        payload: { mode, formatVersion: env.formatVersion, byteLength },
      });
      toast('Backup imported.');
      window.dispatchEvent(new CustomEvent('wean:state-changed'));
      navigate('/today');
    } catch (err) {
      console.error('Restore failed:', err);
      toast('Restore failed — try again.');
      restoreInFlight = false;
    }
  }
}
